import React, { useState, useEffect } from 'react';
import { Star, StarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/use-user';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface PropertyRatingProps {
  propertyId: string;
}

interface Rating {
  id: string;
  rating: number | null;
  review: string | null;
  created_at: string;
  user_id: string | null;
  user_profile?: {
    full_name: string;
  };
}

interface AverageRating {
  average: number;
  total: number;
  breakdown: { [key: number]: number };
}

export const PropertyRating: React.FC<PropertyRatingProps> = ({ propertyId }) => {
  const { user } = useUser();
  const { toast } = useToast();
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [averageRating, setAverageRating] = useState<AverageRating>({
    average: 0,
    total: 0,
    breakdown: {}
  });
  const [userRating, setUserRating] = useState<Rating | null>(null);
  const [newRating, setNewRating] = useState(0);
  const [newReview, setNewReview] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    fetchRatings();
  }, [propertyId]);

  const fetchRatings = async () => {
    try {
      setLoading(true);

      // Public list uses the view that exposes reviewer_name but never user_id
      const { data: ratingsData, error: ratingsError } = await supabase
        .from('public_property_ratings')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false });

      if (ratingsError) throw ratingsError;

      const ratingsWithUserProfiles = (ratingsData || []).map((r: any) => ({
        ...r,
        user_id: null,
        user_profile: r.reviewer_name ? { full_name: r.reviewer_name } : undefined,
      }));

      setRatings(ratingsWithUserProfiles);

      if (ratingsWithUserProfiles.length > 0) {
        const total = ratingsWithUserProfiles.length;
        const sum = ratingsWithUserProfiles.reduce((acc, r) => acc + (r.rating ?? 0), 0);
        const average = sum / total;
        const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
        ratingsWithUserProfiles.forEach(r => {
          if (r.rating) breakdown[r.rating] = (breakdown[r.rating] || 0) + 1;
        });
        setAverageRating({ average, total, breakdown });
      }

      // Current user's own rating — fetch directly (RLS allows owner read)
      if (user) {
        const { data: ownRating } = await supabase
          .from('property_ratings')
          .select('*')
          .eq('property_id', propertyId)
          .eq('user_id', user.id)
          .maybeSingle();
        setUserRating(ownRating || null);
        if (ownRating) {
          setNewRating(ownRating.rating ?? 0);
          setNewReview(ownRating.review || '');
        }
      }
    } catch (error) {
      console.error('Error fetching ratings:', error);
      toast({
        title: "Error",
        description: "Failed to load ratings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const submitRating = async () => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to rate this property",
        variant: "destructive",
      });
      return;
    }

    if (newRating === 0) {
      toast({
        title: "Rating Required",
        description: "Please select a rating",
        variant: "destructive",
      });
      return;
    }

    try {
      setSubmitting(true);

      const ratingData = {
        property_id: propertyId,
        user_id: user.id,
        rating: newRating,
        review: newReview.trim() || null,
      };

      if (userRating) {
        // Update existing rating
        const { error } = await supabase
          .from('property_ratings')
          .update(ratingData)
          .eq('id', userRating.id);

        if (error) throw error;

        toast({
          title: "Rating Updated",
          description: "Your rating has been updated successfully",
        });
      } else {
        // Create new rating
        const { error } = await supabase
          .from('property_ratings')
          .insert(ratingData);

        if (error) throw error;

        toast({
          title: "Rating Submitted",
          description: "Thank you for your rating!",
        });
      }

      setIsDialogOpen(false);
      fetchRatings();
    } catch (error) {
      console.error('Error submitting rating:', error);
      toast({
        title: "Error",
        description: "Failed to submit rating",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating: number, interactive = false, onRate?: (rating: number) => void) => {
    return Array.from({ length: 5 }, (_, index) => {
      const starValue = index + 1;
      const isFilled = starValue <= rating;
      
      return (
        <button
          key={index}
          type="button"
          className={`${interactive ? 'hover:text-accent cursor-pointer' : 'cursor-default'} transition-colors`}
          onClick={() => interactive && onRate && onRate(starValue)}
          disabled={!interactive}
        >
          <Star 
            className={`h-5 w-5 ${isFilled ? 'fill-accent text-accent' : 'text-muted-foreground'}`}
          />
        </button>
      );
    });
  };

  const renderRatingBreakdown = () => {
    const { breakdown, total } = averageRating;
    
    return (
      <div className="space-y-2">
        {[5, 4, 3, 2, 1].map(star => {
          const count = breakdown[star] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          
          return (
            <div key={star} className="flex items-center gap-2 text-sm">
              <span className="w-3 text-muted-foreground">{star}</span>
              <Star className="h-3 w-3 fill-accent text-accent" />
              <div className="flex-1 bg-muted rounded-full h-2">
                 <div 
                   className="bg-accent h-2 rounded-full transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
              <span className="w-8 text-xs text-muted-foreground">{count}</span>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-muted rounded mb-4"></div>
            <div className="h-4 bg-muted rounded mb-2"></div>
            <div className="h-4 bg-muted rounded mb-2"></div>
            <div className="h-4 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Property Ratings</span>
          {user && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  {userRating ? 'Update Rating' : 'Rate Property'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {userRating ? 'Update Your Rating' : 'Rate This Property'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Rating</label>
                    <div className="flex gap-1">
                      {renderStars(newRating, true, setNewRating)}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Review (Optional)</label>
                    <Textarea
                      value={newReview}
                      onChange={(e) => setNewReview(e.target.value)}
                      placeholder="Share your experience with this property..."
                      rows={4}
                    />
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button 
                      onClick={submitRating} 
                      disabled={submitting || newRating === 0}
                      className="flex-1"
                    >
                      {submitting ? 'Submitting...' : (userRating ? 'Update' : 'Submit')}
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => setIsDialogOpen(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {averageRating.total > 0 ? (
          <>
            {/* Summary */}
            <div className="flex items-start gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">
                  {averageRating.average.toFixed(1)}
                </div>
                <div className="flex gap-1 mt-1">
                  {renderStars(Math.round(averageRating.average))}
                </div>
                <div className="text-sm text-muted-foreground mt-1">
                  {averageRating.total} review{averageRating.total !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="flex-1">
                {renderRatingBreakdown()}
              </div>
            </div>

            {/* Individual Reviews */}
            <div className="space-y-4">
              <h4 className="font-semibold">Recent Reviews</h4>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {ratings.filter(rating => rating.review).slice(0, 10).map((rating) => (
                  <div key={rating.id} className="border rounded-lg p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>
                            {rating.user_profile?.full_name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium text-sm">
                            {rating.user_profile?.full_name || 'Anonymous'}
                          </div>
                          <div className="flex gap-1">
                            {renderStars(rating.rating ?? 0)}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(rating.created_at), 'MMM d, yyyy')}
                      </div>
                    </div>
                    {rating.review && (
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {rating.review}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <StarIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No ratings yet</p>
            <p className="text-sm">Be the first to rate this property!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};